// Import the JSON directly to ensure it's bundled
import translationKeys from "./keys.json";
// Primary language list – prioritise the 10 requested languages when cycling
const LANGUAGE_PRIORITY_ORDER = [
    "da",
    "nb",
    "sv",
    "pt",
    "es",
    "zh",
    "ko",
    "ja",
    "en",
    "de",
];
const LANGUAGE_PRIORITY_LOOKUP = new Map(LANGUAGE_PRIORITY_ORDER.map((code, index) => [code, index]));
// Fallback language names if Intl.DisplayNames is not available
const FALLBACK_LANGUAGE_NAMES = {
    da: "Danish",
    "da-dk": "Danish",
    nb: "Norwegian",
    "nb-no": "Norwegian",
    sv: "Swedish",
    "sv-se": "Swedish",
    de: "German",
    "de-de": "German",
    en: "English",
    "en-us": "English",
    pt: "Portuguese",
    "pt-pt": "Portuguese",
    "pt-br": "Portuguese (Brazil)",
    es: "Spanish",
    "es-es": "Spanish",
    "es-mx": "Spanish (Mexico)",
    zh: "Chinese",
    "zh-hans": "Chinese (Simplified)",
    "zh-hant": "Chinese (Traditional)",
    ja: "Japanese",
    "ja-jp": "Japanese",
    ko: "Korean",
    "ko-kr": "Korean",
};
const DISPLAY_NAME_CACHE = new Map();
let displayNameFallbackWarningShown = false;
// Get translation data - prioritize external, fall back to bundled
function getTranslationData() {
    // Check for externally loaded translations first (CDN usage)
    if (typeof window !== "undefined" && window.DS_ONE_TRANSLATIONS) {
        return window.DS_ONE_TRANSLATIONS;
    }
    // Fall back to bundled translations
    return translationKeys;
}
// Cached translation data - use getter to always get fresh data
let translationData = getTranslationData();
const notionStore = new Map();
const defaultLanguage = "en";
function extractPrimarySubtag(code) {
    if (!code) {
        return "";
    }
    return code.toLowerCase().split(/[-_]/)[0] ?? "";
}
function getLanguagePriority(code) {
    const primary = extractPrimarySubtag(code);
    const priority = LANGUAGE_PRIORITY_LOOKUP.get(primary);
    if (typeof priority === "number") {
        return priority;
    }
    return LANGUAGE_PRIORITY_ORDER.length;
}
function sortLanguageCodes(codes) {
    return [...codes].sort((a, b) => {
        const priorityA = getLanguagePriority(a);
        const priorityB = getLanguagePriority(b);
        if (priorityA !== priorityB) {
            return priorityA - priorityB;
        }
        return a.localeCompare(b);
    });
}
function getDisplayNameForLocale(locale, code) {
    const normalizedLocale = locale?.replace("_", "-");
    if (!normalizedLocale) {
        return undefined;
    }
    try {
        let displayNames = DISPLAY_NAME_CACHE.get(normalizedLocale);
        if (!displayNames) {
            displayNames = new Intl.DisplayNames([normalizedLocale], {
                type: "language",
            });
            DISPLAY_NAME_CACHE.set(normalizedLocale, displayNames);
        }
        const cleanedCode = code.replace("_", "-");
        // Try full tag first
        const fullMatch = displayNames.of(cleanedCode);
        if (fullMatch && fullMatch !== cleanedCode) {
            return fullMatch;
        }
        // Then the primary subtag
        const baseMatch = displayNames.of(extractPrimarySubtag(cleanedCode));
        if (baseMatch) {
            return baseMatch;
        }
    }
    catch (error) {
        // Intl.DisplayNames may not be supported; fall back gracefully
        if (!displayNameFallbackWarningShown) {
            console.info("[DS one] Intl.DisplayNames is not available, using fallback language names.");
            displayNameFallbackWarningShown = true;
        }
    }
    return undefined;
}
function getFallbackDisplayName(code) {
    const normalized = code.toLowerCase().replace("_", "-");
    const direct = FALLBACK_LANGUAGE_NAMES[normalized];
    if (direct) {
        return direct;
    }
    const primary = extractPrimarySubtag(normalized);
    return FALLBACK_LANGUAGE_NAMES[primary];
}
export function getLanguageDisplayName(code, options = {}) {
    if (!code) {
        return "";
    }
    const localesToTry = [];
    if (options.locale) {
        localesToTry.push(options.locale);
    }
    if (typeof navigator !== "undefined") {
        if (Array.isArray(navigator.languages)) {
            localesToTry.push(...navigator.languages);
        }
        if (navigator.language) {
            localesToTry.push(navigator.language);
        }
    }
    localesToTry.push(defaultLanguage);
    localesToTry.push("en");
    const tried = new Set();
    for (const locale of localesToTry) {
        if (!locale || tried.has(locale)) {
            continue;
        }
        tried.add(locale);
        const displayName = getDisplayNameForLocale(locale, code);
        if (displayName) {
            return displayName;
        }
    }
    const fallback = getFallbackDisplayName(code);
    if (fallback) {
        return fallback;
    }
    // Fall back to the primary subtag in uppercase
    const primary = extractPrimarySubtag(code);
    return primary ? primary.toUpperCase() : code;
}
const BROWSER_LANGUAGE_PREFERENCES = {
    da: "da",
    "da-dk": "da",
    no: "nb",
    nb: "nb",
    "nb-no": "nb",
    nn: "nn",
    "nn-no": "nn",
    sv: "sv",
    "sv-se": "sv",
    pt: "pt",
    "pt-pt": "pt",
    "pt-br": "pt",
    es: "es",
    "es-es": "es",
    "es-mx": "es",
    zh: "zh",
    "zh-cn": "zh",
    "zh-hans": "zh",
    "zh-tw": "zh",
    "zh-hant": "zh",
    ko: "ko",
    "ko-kr": "ko",
    ja: "ja",
    "ja-jp": "ja",
    en: "en",
    "en-us": "en",
    "en-gb": "en",
    de: "de",
    "de-de": "de",
};
function resolvePreferredLanguage(languageTag) {
    if (!languageTag) {
        return null;
    }
    const normalized = languageTag.toLowerCase().replace("_", "-");
    const directMatch = BROWSER_LANGUAGE_PREFERENCES[normalized];
    if (directMatch) {
        return directMatch;
    }
    const primary = extractPrimarySubtag(normalized);
    const primaryMatch = BROWSER_LANGUAGE_PREFERENCES[primary];
    if (primaryMatch) {
        return primaryMatch;
    }
    return languageTag;
}
// Helper function to get browser language
export function getBrowserLanguage() {
    if (typeof navigator === "undefined") {
        return defaultLanguage;
    }
    const browserLang = navigator.language;
    if (browserLang) {
        const resolved = resolvePreferredLanguage(browserLang);
        if (resolved) {
            return resolved;
        }
    }
    if (Array.isArray(navigator.languages)) {
        for (const candidate of navigator.languages) {
            const resolved = resolvePreferredLanguage(candidate);
            if (resolved) {
                return resolved;
            }
        }
    }
    return defaultLanguage;
}
// Get stored language from localStorage
const storedLanguage = typeof window !== "undefined"
    ? (window.localStorage?.getItem("ds-one:language") ?? undefined)
    : undefined;
// Create a reactive signal for the current language (Portfolio pattern)
export const currentLanguage = {
    value: localStorage.getItem("language") || getBrowserLanguage(),
    set: function (lang) {
        this.value = lang;
        localStorage.setItem("language", lang);
        window.dispatchEvent(new CustomEvent("language-changed", {
            detail: { language: lang },
            bubbles: true,
            composed: true,
        }));
    },
};
// Listen for external translations being loaded
if (typeof window !== "undefined") {
    window.addEventListener("translations-ready", () => {
        // Refresh translation data from external source
        translationData = getTranslationData();
        // Dispatch that translations are loaded
        window.dispatchEvent(new CustomEvent("translations-loaded"));
        window.notionDataLoaded = true;
        // Dispatch language-changed to update all components
        const currentLang = currentLanguage.value;
        window.dispatchEvent(new CustomEvent("language-changed", {
            detail: { language: currentLang },
            bubbles: true,
            composed: true,
        }));
    });
}
// Initialize translations on module load
// Use setTimeout to give other parts of the app time to set up event listeners first
setTimeout(() => {
    // Since we directly imported the data, just dispatch the events
    window.dispatchEvent(new CustomEvent("translations-loaded"));
    window.notionDataLoaded = true;
    // Also dispatch language-changed with the current language
    const currentLang = currentLanguage.value;
    window.dispatchEvent(new CustomEvent("language-changed", {
        detail: { language: currentLang },
        bubbles: true,
        composed: true,
    }));
}, 100);
// Get translation by key
export function translate(key) {
    const lang = currentLanguage.value;
    // Check if key exists in current language
    if (translationData?.[lang]?.[key]) {
        return translationData[lang][key];
    }
    // Try fallback to English
    if (lang !== defaultLanguage && translationData?.[defaultLanguage]?.[key]) {
        return translationData[defaultLanguage][key];
    }
    console.warn(`[translate] No translation found for key "${key}"`);
    return key;
}
export function hasTranslation(key, language = currentLanguage.value) {
    if (!key) {
        return false;
    }
    const langData = translationData?.[language];
    if (langData && Object.prototype.hasOwnProperty.call(langData, key)) {
        return true;
    }
    if (language !== defaultLanguage &&
        translationData?.[defaultLanguage] &&
        Object.prototype.hasOwnProperty.call(translationData[defaultLanguage], key)) {
        return true;
    }
    return false;
}
// Get text - synchronous version for components
export function getText(key) {
    return translate(key);
}
// Get text from translation data (async for compatibility)
export async function getNotionText(key, language = currentLanguage.value) {
    if (!key) {
        return null;
    }
    if (!translationData || !translationData[language]) {
        return null;
    }
    const text = translationData[language][key];
    if (text) {
        return text;
    }
    // Fallback to English
    if (language !== defaultLanguage && translationData[defaultLanguage]?.[key]) {
        return translationData[defaultLanguage][key];
    }
    return null;
}
// Store Notion text (for dynamic updates)
export function setNotionText(key, value, language = currentLanguage.value) {
    if (!key)
        return;
    const bucket = getLanguageBucket(language);
    bucket.set(key, value);
}
function getLanguageBucket(language) {
    if (!notionStore.has(language)) {
        notionStore.set(language, new Map());
    }
    return notionStore.get(language);
}
// Get available languages - dynamically detect from loaded data
export function getAvailableLanguages() {
    // Always get fresh translation data
    const currentData = getTranslationData();
    if (currentData && Object.keys(currentData).length > 0) {
        const languages = Object.keys(currentData);
        return Promise.resolve(sortLanguageCodes(languages));
    }
    return Promise.resolve([defaultLanguage]);
}
// Synchronous version for immediate use
export function getAvailableLanguagesSync() {
    const currentData = getTranslationData();
    if (currentData && Object.keys(currentData).length > 0) {
        return sortLanguageCodes(Object.keys(currentData));
    }
    return [defaultLanguage];
}
// Load translations programmatically (for compatibility)
export function loadTranslations(language, translations) {
    // Since we have static data, this is mainly for compatibility
    console.log(`Loading additional translations for ${language}:`, Object.keys(translations).length, "keys");
}
// Set language (Portfolio pattern)
export function setLanguage(language) {
    // Update the language in localStorage first
    localStorage.setItem("language", language);
    // Then update the signal - this should trigger effects in components
    // that are subscribed to the signal
    currentLanguage.set(language);
    // Dispatch a custom event so non-signal-based components can update
    window.dispatchEvent(new CustomEvent("language-changed", {
        detail: { language },
        bubbles: true,
        composed: true,
    }));
}
